import React, { PureComponent } from 'react'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import {
  mdiImageFrame,
  mdiInvertColors,
  mdiArrowUp,
  mdiArrowDown,
  mdiArrowLeft,
  mdiArrowRight,
  mdiMinus,
  mdiPlus,
  mdiDockLeft,
  mdiDockRight,
  mdiCursorMove,
  mdiClose,
  mdiDrag,
} from '@mdi/js'
import { DSA_SHIFT_STEP } from '../utils/dsaSubtract'
import {
  EDGE_SNAP_PX,
  clearSnapGuide,
  defaultFreeDsaPos,
  probeDsaBesideCine,
  queryCineRect,
  rectFromBox,
  updateSnapGuide,
} from '../utils/panelEdgeSnap'

const iconColor = '#FFFFFF'
const activeColor = '#7CFFB2'
const muted = 'rgba(255,255,255,0.7)'
const STORAGE_KEY = 'udv-dsa-hud-layout-by-mod'
const STORAGE_KEY_LEGACY = 'udv-dsa-hud-layout'

/** Distancia (px) para chasquido a bordes de viewport (no acople entre paneles). */
export const DSA_HUD_SNAP_PX = EDGE_SNAP_PX
/** Opacidad mientras cine está en Play (sin hover). */
export const DSA_HUD_PLAY_OPACITY = 0.3

/** @typedef {'left'|'right'|'free'} DsaHudDock */

/**
 * Normaliza modalidad DICOM a clave de persistencia.
 * @param {string} [modality]
 * @returns {string}
 */
export function normalizeDsaHudModality(modality) {
  const m = String(modality || '')
    .trim()
    .toUpperCase()
  if (!m) return 'DEFAULT'
  if (m === 'XA' || m === 'XRF' || m === 'RF' || m === 'XA/XRF') return 'XA'
  if (m === 'CT' || m === 'CTA') return 'CT'
  if (m === 'MR' || m === 'MRI' || m === 'MRA') return 'MR'
  if (m === 'US' || m === 'IVUS') return 'US'
  if (m === 'PT' || m === 'PET') return 'PT'
  return m.slice(0, 8)
}

function defaultLayout() {
  // Libre por defecto; el acople solo ocurre al tocar bordes con el cine
  return defaultFreeDsaPos()
}

function normalizeLayout(parsed) {
  if (!parsed || typeof parsed !== 'object') return defaultLayout()
  const dock =
    parsed.dock === 'right' || parsed.dock === 'free' || parsed.dock === 'left'
      ? parsed.dock
      : 'free'
  const base = defaultFreeDsaPos()
  return {
    dock,
    x: Number.isFinite(Number(parsed.x)) ? Number(parsed.x) : base.x,
    y: Number.isFinite(Number(parsed.y)) ? Number(parsed.y) : base.y,
  }
}

/**
 * @param {string} [modality]
 * @returns {{ dock: DsaHudDock, x: number, y: number|null }}
 */
export function loadDsaHudLayout(modality) {
  const key = normalizeDsaHudModality(modality)
  try {
    const rawMap = localStorage.getItem(STORAGE_KEY)
    if (rawMap) {
      const map = JSON.parse(rawMap)
      if (map && map[key]) return normalizeLayout(map[key])
      if (map && map.DEFAULT) return normalizeLayout(map.DEFAULT)
    }
    // Migrar layout legacy global → DEFAULT
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY)
    if (legacy) {
      const layout = normalizeLayout(JSON.parse(legacy))
      saveDsaHudLayout(layout, 'DEFAULT')
      return key === 'DEFAULT' ? layout : layout
    }
  } catch (e) {
    /* ignore */
  }
  return defaultLayout()
}

/**
 * @param {{ dock?: string, x?: number, y?: number|null }} layout
 * @param {string} [modality]
 */
export function saveDsaHudLayout(layout, modality) {
  const key = normalizeDsaHudModality(modality)
  const next = normalizeLayout(layout)
  try {
    let map = {}
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      try {
        map = JSON.parse(raw) || {}
      } catch (e) {
        map = {}
      }
    }
    map[key] = next
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch (e) {
    /* ignore */
  }
}

/**
 * Snap magnético a bordes de viewport y zona de barra inferior (explorer/cine).
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 * @returns {{ x: number, y: number, snapped: string|null }}
 */
export function snapDsaHudPosition(x, y, w, h) {
  const vw = window.innerWidth || 360
  const vh = window.innerHeight || 640
  const margin = 4
  const snap = DSA_HUD_SNAP_PX
  // Barra inferior: explorer (~56) + cine (~80) → zona ~140 desde abajo
  let cineBottom = 56
  try {
    const css = getComputedStyle(document.documentElement).getPropertyValue(
      '--udv-cine-bottom'
    )
    const n = parseFloat(css)
    if (Number.isFinite(n)) cineBottom = n
  } catch (e) {
    /* ignore */
  }
  const bottomSafe = cineBottom + 8

  let nx = x
  let ny = y
  let snapped = null

  // Horizontal
  if (nx < snap) {
    nx = margin
    snapped = 'left'
  } else if (nx + w > vw - snap) {
    nx = Math.max(margin, vw - w - margin)
    snapped = 'right'
  }

  // Vertical: top / bottom-nav
  if (ny < snap) {
    ny = margin
    snapped = snapped ? `${snapped}-top` : 'top'
  } else if (ny + h > vh - bottomSafe - snap) {
    ny = Math.max(margin, vh - h - bottomSafe)
    snapped = snapped ? `${snapped}-bottom` : 'bottom'
  }

  // Clamp final
  nx = Math.max(margin, Math.min(vw - w - margin, nx))
  ny = Math.max(margin, Math.min(vh - h - margin, ny))

  return { x: Math.round(nx), y: Math.round(ny), snapped }
}

function readCineBottomPx() {
  let cineBottom = 56
  try {
    const css = getComputedStyle(document.documentElement).getPropertyValue(
      '--udv-cine-bottom'
    )
    const n = parseFloat(css)
    if (Number.isFinite(n)) cineBottom = n
  } catch (e) {
    /* ignore */
  }
  return cineBottom
}

/**
 * Al soltar DSA: acople solo si el borde toca el cine (guía visible).
 * @returns {{ dock: DsaHudDock, x: number, y: number, groupMode?: 'free', coupled?: boolean }}
 */
export function resolveDsaDropLayout(x, y, w, h, snapPreview) {
  if (snapPreview && (snapPreview.side === 'left' || snapPreview.side === 'right')) {
    const cine = queryCineRect()
    // Origen del LinkedPanelsGroup (fila: [DSA?] cine [DSA?])
    const groupX =
      snapPreview.side === 'left'
        ? Math.round(snapPreview.x)
        : Math.round(cine ? cine.left : snapPreview.x - w - 8)
    const groupY = Math.round(
      cine ? Math.min(cine.top, snapPreview.y) : snapPreview.y
    )
    return {
      dock: snapPreview.side,
      x: Math.max(4, groupX),
      y: Math.max(4, groupY),
      groupMode: 'free',
      coupled: true,
    }
  }
  const snapped = snapDsaHudPosition(x, y, w, h)
  return {
    dock: 'free',
    x: snapped.x,
    y: snapped.y,
    groupMode: 'free',
    coupled: false,
  }
}

/**
 * Al soltar cine: acople solo con guía de borde activa.
 */
export function resolveCineDropLayout(x, y, w, h, snapPreview) {
  if (snapPreview && snapPreview.dsaSide) {
    return {
      dock: 'free',
      x: Math.round(snapPreview.x),
      y: Math.round(snapPreview.y),
      dsaSide: snapPreview.dsaSide,
      groupMode: 'free',
      coupled: true,
    }
  }
  const base = resolveLinkedGroupDrop(x, y, w, h)
  return {
    dock: 'free',
    x: base.x,
    y: base.y,
    groupMode: 'free',
    coupled: false,
  }
}

/** Autodock a barra: desactivado (paneles libres). */
export function isNearBottomDock() {
  return false
}

/**
 * Clamp de posición free (por encima de la barra explorer).
 */
export function resolveLinkedGroupDrop(x, y, w, h) {
  const vw = window.innerWidth || 360
  const vh = window.innerHeight || 640
  const cineBottom = readCineBottomPx()
  const cw = Math.max(120, Math.min(Number(w) || 300, Math.min(420, vw * 0.92)))
  const ch = Math.max(80, Math.min(Number(h) || 140, Math.min(280, vh * 0.45)))
  let nx = Number(x)
  let ny = Number(y)
  if (!Number.isFinite(nx)) nx = 8
  if (!Number.isFinite(ny)) ny = Math.max(8, vh - cineBottom - ch - 16)

  nx = Math.max(4, Math.min(vw - cw - 4, nx))
  const maxY = Math.max(4, vh - cineBottom - ch - 8)
  ny = Math.max(4, Math.min(maxY, ny))

  return { mode: 'free', x: Math.round(nx), y: Math.round(ny) }
}

/** @deprecated usar probeDsaBesideCine */
export function resolveStickySideToAnchor(moving, anchor, snapPx = DSA_HUD_SNAP_PX) {
  const hit = probeDsaBesideCine(moving, anchor)
  if (!hit) return null
  return { side: hit.side, groupX: hit.x, groupY: hit.y }
}

function TinyBtn({ title, path, onClick, active, children, size }) {
  const pad = size === 'xs' ? 3 : 4
  const iconSz = size === 'xs' ? '0.95rem' : '1.05rem'
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        style={{
          color: active ? activeColor : iconColor,
          padding: pad,
          background: active
            ? 'rgba(124,255,178,0.14)'
            : 'rgba(255,255,255,0.06)',
          margin: 1,
        }}
      >
        {children || (
          <Icon
            path={path}
            size={iconSz}
            color={active ? activeColor : iconColor}
          />
        )}
      </IconButton>
    </Tooltip>
  )
}

function StepPair({ label, value, onMinus, onPlus, titleMinus, titlePlus }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0,
        margin: '0 2px',
        background: 'rgba(0,0,0,0.25)',
        borderRadius: 6,
        padding: '0 2px',
      }}
    >
      <TinyBtn title={titleMinus} path={mdiMinus} onClick={onMinus} />
      <span
        style={{
          minWidth: 36,
          textAlign: 'center',
          fontSize: 10,
          fontWeight: 600,
          color: muted,
          letterSpacing: 0.2,
          userSelect: 'none',
        }}
      >
        {label}
        {value}
      </span>
      <TinyBtn title={titlePlus} path={mdiPlus} onClick={onPlus} />
    </div>
  )
}

/**
 * Panel DSA desacoplado: dock izq/der, libre + snap magnético,
 * dim en Play, persistencia por modalidad.
 */
class DsaCineHud extends PureComponent {
  _drag = null
  state = { hovered: false }

  fire = (actionId) => {
    if (typeof this.props.onAction === 'function') {
      this.props.onAction(actionId)
    }
  }

  setDock = (dock) => {
    // Desacoplar desde el grupo: conservar posiciones de pantalla
    if (dock === 'free' && this.props.embedded && typeof this.props.onUncouple === 'function') {
      this.props.onUncouple()
      return
    }
    if (typeof this.props.onLayoutChange !== 'function') return
    const rect = this._rootEl && this._rootEl.getBoundingClientRect()
    const x = rect
      ? Math.round(rect.left)
      : defaultFreeDsaPos().x
    const y = rect
      ? Math.round(rect.top)
      : defaultFreeDsaPos().y

    // Libre: desacoplar
    if (dock === 'free') {
      clearSnapGuide()
      if (typeof this.props.onUncouple === 'function') {
        this.props.onUncouple()
        return
      }
      this.props.onLayoutChange({ dock: 'free', x, y })
      return
    }

    // Izq/der: atajo para acoplar al cine si está visible (misma regla que borde)
    const cine = queryCineRect()
    if (!cine) {
      this.props.onLayoutChange({ dock: 'free', x, y })
      return
    }
    const w = rect ? rect.width : 200
    this.props.onLayoutChange({
      dock,
      x:
        dock === 'left'
          ? Math.round(cine.left - w - 8)
          : Math.round(cine.left),
      y: Math.round(cine.top),
      groupMode: 'free',
      coupled: true,
    })
  }

  uncouple = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (typeof this.props.onUncouple === 'function') {
      this.props.onUncouple()
      return
    }
    this.setDock('free')
  }

  onDragHandlePointerDown = (e) => {
    if (this.props.linkedDragHandlers && this.props.linkedDragHandlers.onPointerDown) {
      this.props.linkedDragHandlers.onPointerDown(e)
      return
    }
    const layout = this.props.layout || {}
    let originX
    let originY
    const rect = this._rootEl && this._rootEl.getBoundingClientRect()
    // Si estaba acoplado (left/right), desacoplar a free al empezar a mover solo
    if (layout.dock !== 'free') {
      originX = rect ? rect.left : e.clientX
      originY = rect ? rect.top : e.clientY
      if (typeof this.props.onLayoutChange === 'function') {
        this.props.onLayoutChange({
          dock: 'free',
          x: Math.round(originX),
          y: Math.round(originY),
        })
      }
      if (this._rootEl) {
        this._rootEl.style.position = 'fixed'
        this._rootEl.style.left = `${Math.round(originX)}px`
        this._rootEl.style.top = `${Math.round(originY)}px`
        this._rootEl.style.bottom = 'auto'
        this._rootEl.style.zIndex = '140001'
        this._rootEl.style.margin = '0'
      }
    } else {
      originX = Number.isFinite(Number(layout.x))
        ? Number(layout.x)
        : rect
          ? rect.left
          : e.clientX
      originY = Number.isFinite(Number(layout.y))
        ? Number(layout.y)
        : rect
          ? rect.top
          : e.clientY
    }

    this._drag = {
      startX: e.clientX,
      startY: e.clientY,
      originX,
      originY,
    }
    this._snapPreview = null
    this._pendingPos = { x: Math.round(originX), y: Math.round(originY) }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    e.preventDefault()
    e.stopPropagation()
  }

  onDragHandlePointerMove = (e) => {
    if (this.props.linkedDragHandlers && this.props.linkedDragHandlers.onPointerMove) {
      this.props.linkedDragHandlers.onPointerMove(e)
      return
    }
    if (!this._drag) return
    const dx = e.clientX - this._drag.startX
    const dy = e.clientY - this._drag.startY
    const w = this._rootEl ? this._rootEl.offsetWidth : 200
    const h = this._rootEl ? this._rootEl.offsetHeight : 160
    const rawX = this._drag.originX + dx
    const rawY = this._drag.originY + dy
    const vw = window.innerWidth || 360
    const vh = window.innerHeight || 640
    let x = Math.max(4, Math.min(vw - w - 4, rawX))
    let y = Math.max(4, Math.min(vh - h - 4, rawY))

    const cine = queryCineRect()
    this._snapPreview = null
    if (cine) {
      const hit = probeDsaBesideCine(rectFromBox(x, y, w, h), cine)
      if (hit) {
        this._snapPreview = hit
        x = hit.x
        y = hit.y
        updateSnapGuide(hit.guide)
      } else {
        clearSnapGuide()
      }
    } else {
      clearSnapGuide()
    }

    this._pendingPos = { x: Math.round(x), y: Math.round(y) }
    if (this._rootEl) {
      this._rootEl.style.left = `${this._pendingPos.x}px`
      this._rootEl.style.top = `${this._pendingPos.y}px`
      this._rootEl.style.boxShadow = this._snapPreview
        ? '0 0 0 1px rgba(129,199,132,0.7), 0 2px 14px rgba(0,0,0,0.45)'
        : '0 2px 14px rgba(0,0,0,0.45)'
    }
    e.stopPropagation()
  }

  onDragHandlePointerUp = (e) => {
    if (this.props.linkedDragHandlers && this.props.linkedDragHandlers.onPointerUp) {
      this.props.linkedDragHandlers.onPointerUp(e)
      return
    }
    if (!this._drag) return
    this._drag = null
    const snapPreview = this._snapPreview
    this._snapPreview = null
    clearSnapGuide()
    if (
      this._pendingPos &&
      typeof this.props.onLayoutChange === 'function'
    ) {
      const w = this._rootEl ? this._rootEl.offsetWidth : 200
      const h = this._rootEl ? this._rootEl.offsetHeight : 160
      const drop = resolveDsaDropLayout(
        this._pendingPos.x,
        this._pendingPos.y,
        w,
        h,
        snapPreview
      )
      if (this._rootEl) {
        this._rootEl.style.boxShadow = '0 2px 14px rgba(0,0,0,0.45)'
        if (drop.dock === 'free') {
          this._rootEl.style.left = `${drop.x}px`
          this._rootEl.style.top = `${drop.y}px`
        }
      }
      this.props.onLayoutChange({
        dock: drop.dock,
        x: drop.x,
        y: drop.y,
        groupMode: drop.groupMode,
        coupled: drop.coupled,
      })
    }
    this._pendingPos = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    e.stopPropagation()
  }

  setRootRef = (el) => {
    this._rootEl = el
  }

  onHoverEnter = () => {
    this.setState({ hovered: true })
  }

  onHoverLeave = () => {
    this.setState({ hovered: false })
  }

  render() {
    const {
      gain = 1,
      offset = null,
      noiseFloor = 8,
      invertDSA = false,
      pixelShift = { x: 0, y: 0 },
      maskFrameIndex = 0,
      layout = { dock: 'left' },
      compact = false,
      inPlay = false,
      embedded = false,
    } = this.props

    const dock = layout.dock || 'left'
    const dimmed = !embedded && !!inPlay && !this.state.hovered && !this._drag
    const g =
      gain != null && Number.isFinite(Number(gain))
        ? Number(gain).toFixed(2).replace(/\.?0+$/, '')
        : '1'
    const o =
      offset != null && Number.isFinite(Number(offset))
        ? Math.round(Number(offset))
        : 'auto'
    const n =
      noiseFloor != null && Number.isFinite(Number(noiseFloor))
        ? Math.round(Number(noiseFloor))
        : 8
    const sx = (pixelShift && pixelShift.x) || 0
    const sy = (pixelShift && pixelShift.y) || 0
    const mask1 = maskFrameIndex != null ? Number(maskFrameIndex) + 1 : '?'

    const panelStyle = {
      width: compact ? 168 : 200,
      maxWidth: 'min(46vw, 220px)',
      flex: '0 0 auto',
      flexShrink: 0,
      boxSizing: 'border-box',
      padding: '6px 6px 8px',
      backgroundColor: 'rgba(40, 40, 48, 0.92)',
      borderRadius: 8,
      boxShadow: '0 2px 14px rgba(0,0,0,0.45)',
      border: '1px solid rgba(255,255,255,0.08)',
      pointerEvents: 'auto',
      touchAction: 'manipulation',
      WebkitUserSelect: 'none',
      userSelect: 'none',
      zIndex: 52,
      opacity: dimmed ? DSA_HUD_PLAY_OPACITY : 1,
      transition: 'opacity 0.2s ease',
      position: 'relative',
    }

    // Fuera del grupo: left/right no son posición de pantalla → forzar free visible
    const needsFreePos =
      !embedded &&
      (dock === 'free' || dock === 'left' || dock === 'right')
    if (needsFreePos) {
      panelStyle.position = 'fixed'
      panelStyle.left =
        dock === 'free' && Number.isFinite(Number(layout.x))
          ? Number(layout.x)
          : dock === 'right'
            ? Math.max(8, (window.innerWidth || 360) - 220)
            : 16
      panelStyle.top =
        dock === 'free' && Number.isFinite(Number(layout.y))
          ? Number(layout.y)
          : Math.max(8, (window.innerHeight || 640) - 280)
      panelStyle.bottom = 'auto'
      panelStyle.margin = 0
      panelStyle.zIndex = 140001
    }

    return (
      <div
        ref={this.setRootRef}
        data-dsa-cine-hud="true"
        data-dsa-dock={dock}
        data-dsa-dimmed={dimmed ? '1' : '0'}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={this.onHoverEnter}
        onMouseLeave={this.onHoverLeave}
        onPointerEnter={this.onHoverEnter}
        onPointerLeave={this.onHoverLeave}
        style={panelStyle}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            marginBottom: 4,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            paddingBottom: 4,
          }}
        >
          <div
            onPointerDown={this.onDragHandlePointerDown}
            onPointerMove={this.onDragHandlePointerMove}
            onPointerUp={this.onDragHandlePointerUp}
            onPointerCancel={this.onDragHandlePointerUp}
            title={
              this.props.linkedDragHandlers
                ? 'Mover cine + DSA juntos'
                : 'Arrastrar libre · acerca el borde al cine para acoplar (guía verde)'
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'grab',
              flex: 1,
              minWidth: 0,
              touchAction: 'none',
            }}
          >
            <Icon path={mdiDrag} size="1rem" color={muted} />
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: activeColor,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              DSA · M{mask1}
              {invertDSA === true ? ' INV' : ''}
              {inPlay ? ' · ▶' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', flexShrink: 0 }}>
            {embedded ? (
              <TinyBtn
                size="xs"
                title="Desacoplar · conserva posición y arrastra libre"
                path={mdiClose}
                active={false}
                onClick={this.uncouple}
              />
            ) : (
              <>
                <TinyBtn
                  size="xs"
                  title="Acoplar a la izquierda (o acerca el borde al cine)"
                  path={mdiDockLeft}
                  active={dock === 'left'}
                  onClick={() => this.setDock('left')}
                />
                <TinyBtn
                  size="xs"
                  title="Acoplar a la derecha (o acerca el borde al cine)"
                  path={mdiDockRight}
                  active={dock === 'right'}
                  onClick={() => this.setDock('right')}
                />
                <TinyBtn
                  size="xs"
                  title="Libre · acerca bordes al cine para acoplar"
                  path={mdiCursorMove}
                  active={dock === 'free'}
                  onClick={() => this.setDock('free')}
                />
              </>
            )}
          </div>
        </div>

        <div
          style={{
            fontSize: 9,
            color: muted,
            textAlign: 'center',
            marginBottom: 4,
          }}
        >
          Δ{sx},{sy}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <TinyBtn
            title="Establecer frame actual como máscara"
            path={mdiImageFrame}
            onClick={() => this.fire('mask-current')}
          />
          <TinyBtn
            title={
              invertDSA === true
                ? 'Invert LUT ON (vasos claros)'
                : 'Invert LUT OFF (vasos oscuros · clínico)'
            }
            path={mdiInvertColors}
            active={invertDSA === true}
            onClick={() => this.fire('invert-toggle')}
          />

          <StepPair
            label="G"
            value={g}
            onMinus={() => this.fire('gain-')}
            onPlus={() => this.fire('gain+')}
            titleMinus="Gain −0.25"
            titlePlus="Gain +0.25"
          />
          <StepPair
            label="O"
            value={o}
            onMinus={() => this.fire('offset-')}
            onPlus={() => this.fire('offset+')}
            titleMinus="Offset −64"
            titlePlus="Offset +64"
          />
          <StepPair
            label="N"
            value={n}
            onMinus={() => this.fire('noise-')}
            onPlus={() => this.fire('noise+')}
            titleMinus="Noise floor −2"
            titlePlus="Noise floor +2"
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 4,
          }}
          onAuxClick={(e) => {
            if (e.button === 1) {
              e.preventDefault()
              this.fire('pixel-shift-reset')
            }
          }}
          onMouseDown={(e) => {
            if (e.button === 1) e.preventDefault()
          }}
        >
          <TinyBtn
            title={`Shift ↑ −${DSA_SHIFT_STEP} Y`}
            path={mdiArrowUp}
            onClick={() => this.fire('pixel-shift-y-')}
          />
          <TinyBtn
            title={`Shift ↓ +${DSA_SHIFT_STEP} Y`}
            path={mdiArrowDown}
            onClick={() => this.fire('pixel-shift-y+')}
          />
          <TinyBtn
            title={`Shift ← −${DSA_SHIFT_STEP} X`}
            path={mdiArrowLeft}
            onClick={() => this.fire('pixel-shift-x-')}
          />
          <TinyBtn
            title={`Shift → +${DSA_SHIFT_STEP} X`}
            path={mdiArrowRight}
            onClick={() => this.fire('pixel-shift-x+')}
          />
          <span
            style={{
              fontSize: 9,
              color: muted,
              marginLeft: 4,
              userSelect: 'none',
            }}
          >
            ±{DSA_SHIFT_STEP}
          </span>
        </div>
      </div>
    )
  }
}

export default DsaCineHud
